/**
 * Short keys for the packed record.
 *
 * Once every value is a code or an integer, the field names are the payload:
 * `"distributionFrequency":"m"` is 26 bytes and 23 of them are the key, written
 * out once per record.
 *
 * The map is the reference. Nothing else should carry a short name — the
 * database, the accessors and the site all use the long ones, and this is
 * applied at the very last step and undone at the very first, so a short key
 * never travels further than the artefact.
 *
 * Renaming is recursive: a field means the same thing wherever it appears, so
 * `symbol` inside a holding shortens the same way as the record's own.
 */
export const KEYS: Record<string, string> = {
    symbol: 's',
    name: 'n',
    exchange: 'ex',
    assets: 'as',
    distributionFrequency: 'df',
    holdings: 'hl',
    allocations: 'al',
    distributions: 'di',
    close: 'c',
};

export const LONG_KEYS: Record<string, string> = Object.fromEntries(Object.entries(KEYS).map(([long, short]) => [short, long]));

/**
 * A short key that is already a long key elsewhere would be ambiguous on the
 * way back. None are today; this fails loudly rather than silently if one is
 * ever added.
 */
const collision = Object.keys(KEYS).find((long) => long in LONG_KEYS);
if (collision) throw new Error(`Key map is ambiguous: "${collision}" is both a long key and a short one.`);

const walk = (value: unknown, map: Record<string, string>): unknown => {
    if (Array.isArray(value)) return value.map((v) => walk(v, map));
    if (value === null || typeof value !== 'object') return value;
    // Object.entries and fromEntries both preserve insertion order, so a rename
    // leaves every key exactly where it was — which matters, because the round
    // trip compares serialised records.
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [map[k] ?? k, walk(v, map)]));
};

export const shortenKeys = <T>(record: T): T => walk(record, KEYS) as T;
export const expandKeys = <T>(record: unknown): T => walk(record, LONG_KEYS) as T;
